/**
 * DialogField - A form-aware dialog component that warns users about unsaved changes
 *
 * This component integrates with TanStack Form to provide automatic dirty state checking
 * when closing the dialog. When a user tries to close a dialog with unsaved changes,
 * a confirmation dialog will appear (similar to the Blocker component for route changes).
 *
 * Usage:
 * - Use form.Dialog to create a dialog that integrates with your form
 * - Set blockOnDirty={true} (default) to show confirmation when form is dirty
 * - Customize confirmationTitle and confirmationMessage as needed
 * - Access all standard Dialog sub-components (Content, Header, Footer, Body, Title, etc.)
 *
 * See README or example files for detailed usage examples.
 */

import type { DialogBodyProps, DialogCloseProps, DialogDescriptionProps, DialogFooterProps, DialogHeaderProps, DialogRootProps, DialogTitleProps, DialogTriggerProps } from '@connected/ui/components/dialog';
import type { ComponentPropsWithoutRef } from 'react';

import { Button } from '@connected/ui/components/button';
import { DialogBody, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogRoot, DialogTitle, DialogTrigger } from '@connected/ui/components/dialog';
import { useState } from 'react';

import { useFormContext } from '../../hooks/use-form-context';

export type DialogFieldProps = Omit<DialogRootProps, 'open' | 'onOpenChange'> & {
    /**
     * Whether to block closing when form is dirty
     * @default true
     */
    blockOnDirty?: boolean;

    /**
     * Controlled open state
     */
    open?: boolean;
    /**
     * Callback when open state changes
     */
    onOpenChange?: (open: boolean) => void;
    /**
     * Custom confirmation message
     * @default "Weet je zeker dat je wilt sluiten? Je wijzigingen zullen verloren gaan."
     */
    confirmationMessage?: string;
    /**
     * Custom confirmation title
     * @default "Niet opgeslagen wijzigingen"
     */
    confirmationTitle?: string;
};

function DialogFieldRoot(props: DialogFieldProps) {
    const {
        blockOnDirty = true,
        open: controlledOpen,
        onOpenChange: controlledOnOpenChange,
        confirmationMessage = 'Weet je zeker dat je wilt sluiten? Je wijzigingen zullen verloren gaan.',
        confirmationTitle = 'Niet opgeslagen wijzigingen',
        ...restProps
    } = props;

    const form = useFormContext();
    const [internalOpen, setInternalOpen] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);

    // Use controlled state if provided, otherwise use internal state
    const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;
    const setIsOpen = controlledOnOpenChange || setInternalOpen;

    const handleOpenChange = (open: boolean) => {
        if (!open && blockOnDirty) {
            // Trying to close - check if form is dirty
            const isDirty = form.state.isDirty;
            if (isDirty) {
                // Show confirmation dialog
                setShowConfirmation(true);
                return;
            }
        }

        // Either opening or closing without dirty state
        setIsOpen(open);
    };

    const handleConfirmClose = () => {
        setShowConfirmation(false);
        setIsOpen(false);
        // Optionally reset form
        // form.reset();
    };

    const handleCancelClose = () => {
        setShowConfirmation(false);
    };

    return (
        <>
            <DialogRoot open={isOpen} onOpenChange={handleOpenChange} {...restProps} />

            {/* Confirmation Dialog */}
            <DialogRoot open={showConfirmation} onOpenChange={handleCancelClose}>
                <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                        <DialogTitle>{confirmationTitle}</DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                        <p>{confirmationMessage}</p>
                    </div>
                    <DialogFooter>
                        {/* TODO: translations */}
                        <Button variant="outline" onClick={handleCancelClose}>Annuleren</Button>
                        <Button onClick={handleConfirmClose}>Bevestigen</Button>
                    </DialogFooter>
                </DialogContent>
            </DialogRoot>
        </>
    );
}
DialogFieldRoot.displayName = 'DialogFieldRoot';

export type DialogFieldContentProps = ComponentPropsWithoutRef<typeof DialogContent>;

function DialogFieldContent(props: DialogFieldContentProps) {
    return <DialogContent {...props} />;
}

DialogFieldContent.displayName = 'DialogFieldContent';

export type DialogFieldTriggerProps = DialogTriggerProps;

function DialogFieldTrigger(props: DialogFieldTriggerProps) {
    return <DialogTrigger {...props} />;
}

DialogFieldTrigger.displayName = 'DialogFieldTrigger';

export type DialogFieldHeaderProps = DialogHeaderProps;

function DialogFieldHeader(props: DialogFieldHeaderProps) {
    return <DialogHeader {...props} />;
}

DialogFieldHeader.displayName = 'DialogFieldHeader';

export type DialogFieldFooterProps = DialogFooterProps;

function DialogFieldFooter(props: DialogFieldFooterProps) {
    return <DialogFooter {...props} />;
}

DialogFieldFooter.displayName = 'DialogFieldFooter';

export type DialogFieldBodyProps = DialogBodyProps;

function DialogFieldBody(props: DialogFieldBodyProps) {
    return <DialogBody {...props} />;
}

DialogFieldBody.displayName = 'DialogFieldBody';

export type DialogFieldTitleProps = DialogTitleProps;

function DialogFieldTitle(props: DialogFieldTitleProps) {
    return <DialogTitle {...props} />;
}

DialogFieldTitle.displayName = 'DialogFieldTitle';

export type DialogFieldDescriptionProps = DialogDescriptionProps;

function DialogFieldDescription(props: DialogFieldDescriptionProps) {
    return <DialogDescription {...props} />;
}

DialogFieldDescription.displayName = 'DialogFieldDescription';

export type DialogFieldCloseProps = DialogCloseProps;

function DialogFieldClose(props: DialogFieldCloseProps) {
    return <DialogClose {...props} />;
}

DialogFieldClose.displayName = 'DialogFieldClose';

const DialogField = Object.assign(DialogFieldRoot, {
    Content: DialogFieldContent,
    Trigger: DialogFieldTrigger,
    Header: DialogFieldHeader,
    Footer: DialogFieldFooter,
    Body: DialogFieldBody,
    Title: DialogFieldTitle,
    Description: DialogFieldDescription,
    Close: DialogFieldClose,
});

export { DialogField };
